import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import dentalAd from "./dentalb.uk.png";

const POST_SPEECH_DELAY_MS = 5000;
const DETECTION_INTERVAL_MS = 90;

const BG = "#00B4A6";
const BG_DARK = "#00897B";
const BG_LIGHT = "#35D6C8";
const CARD = "rgba(255,255,255,0.16)";
const CARD_BORDER = "rgba(255,255,255,0.24)";
const TEXT = "#ffffff";
const TEXT_SOFT = "rgba(255,255,255,0.88)";
const ACCENT = "#FFE082";
const SHADOW = "0 18px 48px rgba(0,0,0,0.18)";

export default function FaceJokesPage() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const faceLandmarkerRef = useRef(null);
  const rafRef = useRef(null);

  const voicesRef = useRef([]);
  const activeRef = useRef(false);
  const speechStateRef = useRef("idle"); // idle | speaking | cooling
  const cooldownUntilRef = useRef(0);

  const stableMoodRef = useRef({});
  const lastVideoTimeRef = useRef(-1);
  const lastDetectionAtRef = useRef(0);
  const lastMoodRef = useRef("");
  const currentLineRef = useRef("");
  const noFaceSinceRef = useRef(0);

  const statusRef = useRef("");
  const voiceStatusRef = useRef("");

  const [started, setStarted] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [permissionError, setPermissionError] = useState("");
  const [status, setStatus] = useState("Tap Start Mood Mirror to begin.");
  const [engineState, setEngineState] = useState("Waiting");
  const [moodName, setMoodName] = useState("Expression radar idle");
  const [confidence, setConfidence] = useState(0);
  const [spokenLine, setSpokenLine] = useState(
    "Tap Start Mood Mirror, show your face, and I will describe the mood I can see."
  );

  const [voiceStatus, setVoiceStatus] = useState("Loading voices...");
  const [selectedVoiceName, setSelectedVoiceName] = useState("");
  const [manualVoiceName, setManualVoiceName] = useState("");
  const [availableVoices, setAvailableVoices] = useState([]);

  const moodBank = useMemo(() => buildMoodBank(), []);

  const setStatusOnce = useCallback((next) => {
    if (statusRef.current !== next) {
      statusRef.current = next;
      setStatus(next);
    }
  }, []);

  const setVoiceStatusOnce = useCallback((next) => {
    if (voiceStatusRef.current !== next) {
      voiceStatusRef.current = next;
      setVoiceStatus(next);
    }
  }, []);

  useEffect(() => {
    const loadVoices = () => {
      if (!window.speechSynthesis) {
        setVoiceStatusOnce("Speech synthesis is unavailable on this browser");
        return;
      }

      const voices = window.speechSynthesis.getVoices() || [];
      voicesRef.current = voices;
      setAvailableVoices(voices);

      const chosen = pickBestVoice(voices, manualVoiceName);
      setSelectedVoiceName(chosen?.name || "");

      setVoiceStatusOnce(
        chosen
          ? `Voice ready: ${chosen.name}`
          : "Voice will appear when the browser finishes loading it"
      );
    };

    loadVoices();

    if (window.speechSynthesis) {
      window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    }

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
      }
    };
  }, [manualVoiceName, setVoiceStatusOnce]);

  const getBlendshapeScore = useCallback((blendshapes, name) => {
    if (!blendshapes?.length || !blendshapes[0]?.categories) return 0;
    const found = blendshapes[0].categories.find(
      (item) => item.categoryName === name
    );
    return found ? found.score : 0;
  }, []);

  const speakMoodLine = useCallback(
    (line, mood) => {
      currentLineRef.current = line;
      lastMoodRef.current = mood.id;

      setSpokenLine(line);
      setMoodName(mood.label);

      if (!window.speechSynthesis) {
        speechStateRef.current = "cooling";
        cooldownUntilRef.current = Date.now() + POST_SPEECH_DELAY_MS;
        setEngineState("Waiting 5 seconds");
        setVoiceStatusOnce("Speech synthesis unavailable");
        return;
      }

      try {
        window.speechSynthesis.cancel();

        const utter = new SpeechSynthesisUtterance(cleanSpeechText(line));
        const chosenVoice = pickBestVoice(voicesRef.current, manualVoiceName);

        if (chosenVoice) {
          utter.voice = chosenVoice;
          utter.lang = chosenVoice.lang || "en-GB";
          utter.voiceURI = chosenVoice.voiceURI;
          setSelectedVoiceName(chosenVoice.name);
        } else {
          utter.lang = "en-GB";
        }

        const expressive = getExpressiveSpeechSettings(line, chosenVoice);
        utter.rate = expressive.rate;
        utter.pitch = expressive.pitch;
        utter.volume = 1;

        utter.onstart = () => {
          speechStateRef.current = "speaking";
          setEngineState("Speaking");
          setVoiceStatusOnce(
            chosenVoice ? `Speaking with ${chosenVoice.name}` : "Speaking"
          );
        };

        utter.onend = () => {
          speechStateRef.current = "cooling";
          cooldownUntilRef.current = Date.now() + POST_SPEECH_DELAY_MS;
          setEngineState("Waiting 5 seconds");
          setVoiceStatusOnce(
            chosenVoice ? `Voice ready: ${chosenVoice.name}` : "Voice ready"
          );
        };

        utter.onerror = () => {
          speechStateRef.current = "cooling";
          cooldownUntilRef.current = Date.now() + POST_SPEECH_DELAY_MS;
          setEngineState("Waiting 5 seconds");
          setVoiceStatusOnce("Voice hiccup, recovering");
        };

        speechStateRef.current = "speaking";
        window.speechSynthesis.speak(utter);
      } catch (error) {
        console.error(error);
        speechStateRef.current = "cooling";
        cooldownUntilRef.current = Date.now() + POST_SPEECH_DELAY_MS;
        setEngineState("Waiting 5 seconds");
        setVoiceStatusOnce("Voice unavailable");
      }
    },
    [manualVoiceName, setVoiceStatusOnce]
  );

  const handleFaceResult = useCallback(
    (result) => {
      const faceDetected = !!result?.faceLandmarks?.length;

      if (!faceDetected) {
        stableMoodRef.current = {};
        const now = Date.now();

        if (!noFaceSinceRef.current) {
          noFaceSinceRef.current = now;
        }

        if (now - noFaceSinceRef.current > 1200) {
          setStatusOnce("No face detected. Move gently into the frame.");
          setMoodName("No face detected");
          setConfidence(0);

          if (speechStateRef.current === "idle") {
            setEngineState("Watching");
          }
        }

        return;
      }

      noFaceSinceRef.current = Date.now();
      setStatusOnce("Face detected. Reading the visible expression.");

      if (speechStateRef.current === "speaking") {
        setEngineState("Speaking");
        return;
      }

      if (speechStateRef.current === "cooling") {
        if (Date.now() >= cooldownUntilRef.current) {
          speechStateRef.current = "idle";
          setEngineState("Watching");
        } else {
          setEngineState("Waiting 5 seconds");
          return;
        }
      }

      const features = getFaceFeatures(result, getBlendshapeScore);
      const detected = classifyMood(features, moodBank, stableMoodRef, lastMoodRef);

      if (!detected) {
        setMoodName("Reading expression...");
        setConfidence(0);
        return;
      }

      setMoodName(detected.mood.label);
      setConfidence(detected.confidence);

      if (speechStateRef.current !== "idle") return;

      const line = chooseMoodLine(detected.mood, currentLineRef);
      speakMoodLine(line, detected.mood);
    },
    [getBlendshapeScore, moodBank, setStatusOnce, speakMoodLine]
  );

  const predictLoop = useCallback(() => {
    if (!activeRef.current) return;

    const video = videoRef.current;
    const faceLandmarker = faceLandmarkerRef.current;

    if (!video || !faceLandmarker || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(predictLoop);
      return;
    }

    const now = performance.now();

    if (now - lastDetectionAtRef.current >= DETECTION_INTERVAL_MS) {
      lastDetectionAtRef.current = now;

      if (video.currentTime !== lastVideoTimeRef.current) {
        lastVideoTimeRef.current = video.currentTime;

        try {
          const result = faceLandmarker.detectForVideo(video, now);
          handleFaceResult(result);
        } catch (error) {
          console.error(error);
          setStatusOnce("Face detector paused for a moment, recovering.");
        }
      }
    }

    rafRef.current = requestAnimationFrame(predictLoop);
  }, [handleFaceResult, setStatusOnce]);

  const stopMirror = useCallback(() => {
    activeRef.current = false;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (faceLandmarkerRef.current) {
      faceLandmarkerRef.current.close?.();
      faceLandmarkerRef.current = null;
    }

    stableMoodRef.current = {};
    speechStateRef.current = "idle";

    setStarted(false);
    setCameraReady(false);
    setEngineState("Stopped");
    setMoodName("Expression radar idle");
    setConfidence(0);
    setStatusOnce("Mood Mirror stopped. Tap Start Mood Mirror to begin again.");
  }, [setStatusOnce]);

  const startMirror = useCallback(async () => {
    if (started) return;

    setPermissionError("");
    setStarted(true);
    setEngineState("Loading");
    setStatusOnce("Loading face mood detector...");

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera API unavailable");
      }

      if (window.speechSynthesis) {
        window.speechSynthesis.resume();
        const voices = window.speechSynthesis.getVoices() || [];
        voicesRef.current = voices;
        setAvailableVoices(voices);
      }

      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
      );

      faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
        vision,
        {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
          },
          runningMode: "VIDEO",
          numFaces: 1,
          outputFaceBlendshapes: true,
          minFaceDetectionConfidence: 0.52,
          minFacePresenceConfidence: 0.52,
          minTrackingConfidence: 0.52,
        }
      );

      setStatusOnce("Requesting camera permission...");

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 960 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        await videoRef.current.play();
      }

      activeRef.current = true;
      stableMoodRef.current = {};
      lastVideoTimeRef.current = -1;
      lastDetectionAtRef.current = 0;
      noFaceSinceRef.current = Date.now();
      speechStateRef.current = "idle";

      setCameraReady(true);
      setEngineState("Watching");
      setMoodName("Reading expression...");
      setConfidence(0);
      setStatusOnce("Camera ready. Show me your expression.");
      setSpokenLine(
        "I am ready. I will estimate the visible mood, say it kindly, then wait 5 seconds."
      );

      rafRef.current = requestAnimationFrame(predictLoop);
    } catch (err) {
      console.error(err);
      setPermissionError(
        "Camera access failed. This page needs HTTPS, camera permission, and a browser that supports getUserMedia."
      );
      setStatusOnce("Could not start camera.");
      setEngineState("Error");
      setStarted(false);
      activeRef.current = false;
    }
  }, [predictLoop, setStatusOnce, started]);

  const previewVoice = useCallback(() => {
    if (!window.speechSynthesis) return;

    const chosenVoice = pickBestVoice(voicesRef.current, manualVoiceName);
    window.speechSynthesis.cancel();

    const utter = new SpeechSynthesisUtterance(
      "Voice check. I am the Mood Mirror, here to read the face gently and add a tiny joke."
    );

    if (chosenVoice) {
      utter.voice = chosenVoice;
      utter.lang = chosenVoice.lang || "en-GB";
      utter.voiceURI = chosenVoice.voiceURI;
    } else {
      utter.lang = "en-GB";
    }

    utter.rate = 0.93;
    utter.pitch = 1.06;
    utter.volume = 1;

    window.speechSynthesis.speak(utter);
  }, [manualVoiceName]);

  useEffect(() => {
    return () => {
      activeRef.current = false;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close?.();
      }
    };
  }, []);

  return (
    <div className="mood-page">
      <style>{pageStyles}</style>

      <div className="mood-shell">
        <main className="main-card">
          <header className="hero-row">
            <div>
              <div className="eyebrow">AI visible mood mirror</div>
              <h1>Mirror of Cheer</h1>
              <p className="intro">
                I estimate the expression on your face, name the visible mood,
                and turn it into a kind little joke you can hear.
              </p>
            </div>

            <div className="engine-card">
              <span>Engine</span>
              <strong>{engineState}</strong>
            </div>
          </header>

          <section className="camera-wrap">
            {!started && (
              <div className="start-overlay">
                <div className="start-panel">
                  <div className="spark">✦</div>
                  <h2>Start Mood Mirror</h2>
                  <p>
                    On mobile, tapping start helps the camera and voice start
                    properly.
                  </p>
                  <button className="primary-btn" onClick={startMirror}>
                    Start Mood Mirror
                  </button>
                </div>
              </div>
            )}

            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="mirror-video"
            />

            <div className="pill-row">
              <StatusPill label={cameraReady ? "Camera ready" : "Camera waiting"} />
              <StatusPill label={engineState} accent />
              <StatusPill label={moodName} />
            </div>
          </section>

          <section className="mood-result-card">
            <div className="joke-label">Visible mood detected</div>

            <div className="mood-row">
              <div>
                <div className="mood-name">{moodName}</div>
                <div className="confidence-line">
                  Confidence: {confidence > 0 ? `${confidence}%` : "Waiting"}
                </div>
              </div>

              <div className="confidence-ring">
                <span>{confidence > 0 ? confidence : "..."}</span>
              </div>
            </div>

            <div className="spoken-line">{spokenLine}</div>

            <p>
              I speak one mood line, wait 5 seconds after finishing, then read
              the next visible expression.
            </p>
          </section>

          <section className="controls-grid">
            <InfoCard label="Status" value={status} />
            <InfoCard label="Voice" value={voiceStatus} />
            <InfoCard
              label="Selected Voice"
              value={selectedVoiceName || "System default"}
            />
            <InfoCard label="Mood Types" value={`${moodBank.length} expression moods`} />
          </section>

          <section className="voice-panel">
            <div>
              <div className="small-label">Voice selector</div>
              <p>
                Auto chooses the most natural English voice available on the
                device. Try Microsoft Edge on Windows for very natural voices.
              </p>
            </div>

            <div className="voice-actions">
              <select
                value={manualVoiceName}
                onChange={(e) => setManualVoiceName(e.target.value)}
                aria-label="Choose voice"
              >
                <option value="">Auto natural voice</option>
                {availableVoices
                  .filter((voice) => (voice.lang || "").toLowerCase().startsWith("en"))
                  .map((voice) => (
                    <option key={`${voice.name}-${voice.lang}`} value={voice.name}>
                      {voice.name} ({voice.lang})
                    </option>
                  ))}
              </select>

              <button className="secondary-btn" onClick={previewVoice}>
                Preview voice
              </button>

              {started ? (
                <button className="danger-btn" onClick={stopMirror}>
                  Stop
                </button>
              ) : (
                <button className="secondary-btn" onClick={startMirror}>
                  Start
                </button>
              )}
            </div>
          </section>

          {permissionError && <p className="error-text">{permissionError}</p>}
        </main>

        <aside className="ad-card">
          <div className="ad-label">Sponsored smile</div>

          <a
            href="https://www.dentalb.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="ad-link"
          >
            <img src={dentalAd} alt="Dental B UK advertisement" />
          </a>

          <div className="ad-copy">
            <h2>Dental services by Dental B UK</h2>
            <p>
              Click the ad to visit <strong>www.dentalb.uk</strong>.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function StatusPill({ label, accent = false }) {
  return (
    <div className={accent ? "status-pill status-pill-accent" : "status-pill"}>
      {label}
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="info-card">
      <div>{label}</div>
      <strong>{value}</strong>
    </div>
  );
}

function pickBestVoice(voices, preferredName = "") {
  if (!voices?.length) return null;

  if (preferredName) {
    const preferred = voices.find((voice) => voice.name === preferredName);
    if (preferred) return preferred;
  }

  const scoreVoice = (voice) => {
    let score = 0;
    const name = `${voice.name} ${voice.voiceURI}`.toLowerCase();
    const lang = (voice.lang || "").toLowerCase();

    if (lang.startsWith("en-gb")) score += 55;
    else if (lang.startsWith("en-us")) score += 45;
    else if (lang.startsWith("en-au") || lang.startsWith("en-ca")) score += 34;
    else if (lang.startsWith("en")) score += 25;

    if (/natural|neural|online|premium|enhanced/.test(name)) score += 65;
    if (/microsoft|google|apple/.test(name)) score += 22;

    if (
      /sonia|libby|aria|ava|jenny|samantha|victoria|serena|zira|moira|allison|karen|hazel|susan|ryan|guy/.test(
        name
      )
    ) {
      score += 26;
    }

    if (/robot|compact|novelty|whisper/.test(name)) score -= 25;

    return score;
  };

  return [...voices].sort((a, b) => scoreVoice(b) - scoreVoice(a))[0] || null;
}

function cleanSpeechText(text) {
  return text.replace(/\s+/g, " ").trim();
}

function getExpressiveSpeechSettings(text, voice) {
  const lower = text.toLowerCase();
  const voiceName = `${voice?.name || ""} ${voice?.voiceURI || ""}`.toLowerCase();

  let rate = 0.93;
  let pitch = 1.06;

  if (/natural|neural|online|premium/.test(voiceName)) {
    rate += 0.01;
    pitch -= 0.01;
  }

  if (/happy|joy|playful|cheeky|excited|bright|flirty|amused/.test(lower)) {
    pitch += 0.04;
    rate += 0.01;
  }

  if (/sad|worried|peaceful|calm|serene|gentle/.test(lower)) {
    rate -= 0.04;
    pitch -= 0.02;
  }

  if (/surprised|shocked|dramatic|amazed|astonished/.test(lower)) {
    rate += 0.02;
    pitch += 0.03;
  }

  if (text.length > 120) rate -= 0.04;

  return {
    rate: Math.max(0.84, Math.min(1.04, rate)),
    pitch: Math.max(0.94, Math.min(1.25, pitch)),
  };
}

function getBlend(blendshapes, getBlendshapeScore, name) {
  return getBlendshapeScore(blendshapes, name);
}

function getAverage(a, b) {
  return (a + b) / 2;
}

function getFaceFeatures(result, getBlendshapeScore) {
  const blendshapes = result.faceBlendshapes || [];
  const landmarks = result.faceLandmarks?.[0] || [];
  const pose = getHeadPoseSignals(landmarks);

  const s = {
    mouthSmileLeft: getBlend(blendshapes, getBlendshapeScore, "mouthSmileLeft"),
    mouthSmileRight: getBlend(blendshapes, getBlendshapeScore, "mouthSmileRight"),

    mouthFrownLeft: getBlend(blendshapes, getBlendshapeScore, "mouthFrownLeft"),
    mouthFrownRight: getBlend(blendshapes, getBlendshapeScore, "mouthFrownRight"),

    jawOpen: getBlend(blendshapes, getBlendshapeScore, "jawOpen"),
    mouthPucker: getBlend(blendshapes, getBlendshapeScore, "mouthPucker"),
    mouthFunnel: getBlend(blendshapes, getBlendshapeScore, "mouthFunnel"),

    browOuterUpLeft: getBlend(blendshapes, getBlendshapeScore, "browOuterUpLeft"),
    browOuterUpRight: getBlend(blendshapes, getBlendshapeScore, "browOuterUpRight"),
    browInnerUp: getBlend(blendshapes, getBlendshapeScore, "browInnerUp"),
    browDownLeft: getBlend(blendshapes, getBlendshapeScore, "browDownLeft"),
    browDownRight: getBlend(blendshapes, getBlendshapeScore, "browDownRight"),

    eyeBlinkLeft: getBlend(blendshapes, getBlendshapeScore, "eyeBlinkLeft"),
    eyeBlinkRight: getBlend(blendshapes, getBlendshapeScore, "eyeBlinkRight"),
    eyeSquintLeft: getBlend(blendshapes, getBlendshapeScore, "eyeSquintLeft"),
    eyeSquintRight: getBlend(blendshapes, getBlendshapeScore, "eyeSquintRight"),
    eyeWideLeft: getBlend(blendshapes, getBlendshapeScore, "eyeWideLeft"),
    eyeWideRight: getBlend(blendshapes, getBlendshapeScore, "eyeWideRight"),

    cheekPuff: getBlend(blendshapes, getBlendshapeScore, "cheekPuff"),
    cheekSquintLeft: getBlend(blendshapes, getBlendshapeScore, "cheekSquintLeft"),
    cheekSquintRight: getBlend(blendshapes, getBlendshapeScore, "cheekSquintRight"),

    mouthPressLeft: getBlend(blendshapes, getBlendshapeScore, "mouthPressLeft"),
    mouthPressRight: getBlend(blendshapes, getBlendshapeScore, "mouthPressRight"),
    mouthStretchLeft: getBlend(blendshapes, getBlendshapeScore, "mouthStretchLeft"),
    mouthStretchRight: getBlend(blendshapes, getBlendshapeScore, "mouthStretchRight"),
    mouthDimpleLeft: getBlend(blendshapes, getBlendshapeScore, "mouthDimpleLeft"),
    mouthDimpleRight: getBlend(blendshapes, getBlendshapeScore, "mouthDimpleRight"),

    mouthRollLower: getBlend(blendshapes, getBlendshapeScore, "mouthRollLower"),
    mouthRollUpper: getBlend(blendshapes, getBlendshapeScore, "mouthRollUpper"),
    mouthShrugUpper: getBlend(blendshapes, getBlendshapeScore, "mouthShrugUpper"),
    mouthShrugLower: getBlend(blendshapes, getBlendshapeScore, "mouthShrugLower"),

    mouthLowerDownLeft: getBlend(blendshapes, getBlendshapeScore, "mouthLowerDownLeft"),
    mouthLowerDownRight: getBlend(blendshapes, getBlendshapeScore, "mouthLowerDownRight"),
    mouthUpperUpLeft: getBlend(blendshapes, getBlendshapeScore, "mouthUpperUpLeft"),
    mouthUpperUpRight: getBlend(blendshapes, getBlendshapeScore, "mouthUpperUpRight"),

    noseSneerLeft: getBlend(blendshapes, getBlendshapeScore, "noseSneerLeft"),
    noseSneerRight: getBlend(blendshapes, getBlendshapeScore, "noseSneerRight"),
  };

  const smile = getAverage(s.mouthSmileLeft, s.mouthSmileRight);
  const frown = getAverage(s.mouthFrownLeft, s.mouthFrownRight);
  const browUp = getAverage(s.browOuterUpLeft, s.browOuterUpRight);
  const browDown = getAverage(s.browDownLeft, s.browDownRight);
  const blink = getAverage(s.eyeBlinkLeft, s.eyeBlinkRight);
  const squint = getAverage(s.eyeSquintLeft, s.eyeSquintRight);
  const eyeWide = getAverage(s.eyeWideLeft, s.eyeWideRight);
  const cheekSquint = getAverage(s.cheekSquintLeft, s.cheekSquintRight);
  const press = getAverage(s.mouthPressLeft, s.mouthPressRight);
  const stretch = getAverage(s.mouthStretchLeft, s.mouthStretchRight);
  const dimple = getAverage(s.mouthDimpleLeft, s.mouthDimpleRight);
  const lipRoll = getAverage(s.mouthRollLower, s.mouthRollUpper);
  const mouthShrug = getAverage(s.mouthShrugUpper, s.mouthShrugLower);
  const mouthLowerDown = getAverage(s.mouthLowerDownLeft, s.mouthLowerDownRight);
  const mouthUpperUp = getAverage(s.mouthUpperUpLeft, s.mouthUpperUpRight);
  const noseSneer = getAverage(s.noseSneerLeft, s.noseSneerRight);
  const pucker = Math.max(s.mouthPucker, s.mouthFunnel);

  const smileAsymmetry = Math.abs(s.mouthSmileLeft - s.mouthSmileRight);
  const browAsymmetry = Math.abs(s.browOuterUpLeft - s.browOuterUpRight);

  return {
    ...s,
    ...pose,

    smile,
    frown,
    browUp,
    browDown,
    blink,
    squint,
    eyeWide,
    cheekSquint,
    press,
    stretch,
    dimple,
    lipRoll,
    mouthShrug,
    mouthLowerDown,
    mouthUpperUp,
    noseSneer,
    pucker,
    smileAsymmetry,
    browAsymmetry,

    leftWink: s.eyeBlinkLeft > 0.76 && s.eyeBlinkRight < 0.46,
    rightWink: s.eyeBlinkRight > 0.76 && s.eyeBlinkLeft < 0.46,
    bigSmile: smile > 0.58,
    softSmile: smile > 0.28 && smile <= 0.58,
    mouthOpen: s.jawOpen > 0.23,
    jawDrop: s.jawOpen > 0.48,
    neutral:
      smile < 0.2 &&
      frown < 0.12 &&
      s.jawOpen < 0.18 &&
      browUp < 0.18 &&
      browDown < 0.18 &&
      blink < 0.55,
  };
}

function getHeadPoseSignals(landmarks) {
  if (!landmarks?.length) {
    return {
      headTiltLeft: false,
      headTiltRight: false,
      headTurnLeft: false,
      headTurnRight: false,
    };
  }

  const leftEye = landmarks[33];
  const rightEye = landmarks[263];
  const nose = landmarks[1];
  const leftCheek = landmarks[234];
  const rightCheek = landmarks[454];

  let headTiltLeft = false;
  let headTiltRight = false;
  let headTurnLeft = false;
  let headTurnRight = false;

  if (leftEye && rightEye) {
    const dy = rightEye.y - leftEye.y;
    headTiltLeft = dy > 0.025;
    headTiltRight = dy < -0.025;
  }

  if (leftCheek && rightCheek && nose) {
    const midpointX = (leftCheek.x + rightCheek.x) / 2;
    const offset = nose.x - midpointX;
    headTurnRight = offset > 0.032;
    headTurnLeft = offset < -0.032;
  }

  return { headTiltLeft, headTiltRight, headTurnLeft, headTurnRight };
}

function classifyMood(features, moodBank, stableMoodRef, lastMoodRef) {
  const prev = stableMoodRef.current || {};
  const next = {};
  const hits = [];

  for (const mood of moodBank) {
    let hit = false;

    try {
      hit = Boolean(mood.rule(features));
    } catch {
      hit = false;
    }

    next[mood.id] = hit ? (prev[mood.id] || 0) + 1 : 0;

    if (hit) {
      hits.push({
        mood,
        frames: next[mood.id],
      });
    }
  }

  stableMoodRef.current = next;

  const stableHits = hits.filter((item) => item.frames >= item.mood.stability);
  if (!stableHits.length) return null;

  const nonRepeat =
    stableHits.find((item) => item.mood.id !== lastMoodRef.current) ||
    stableHits[0];

  const confidence = Math.max(
    54,
    Math.min(96, Math.round(50 + nonRepeat.frames * 8 + nonRepeat.mood.weight))
  );

  return {
    mood: nonRepeat.mood,
    confidence,
  };
}

function chooseMoodLine(mood, currentLineRef) {
  const options = mood.lines || [];
  const shuffled = shuffle([...options]);
  const selected =
    shuffled.find((line) => line !== currentLineRef.current) ||
    shuffled[0] ||
    `Your visible mood looks ${mood.label}. Even the camera paused to take notes.`;

  if (selected.includes("{mood}")) {
    return selected.replaceAll("{mood}", mood.label);
  }

  return `Your visible mood looks ${mood.label}. ${selected}`;
}

function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}

function buildMoodBank() {
  return [
    {
      id: "dramaticSurprise",
      label: "Dramatically surprised",
      stability: 2,
      weight: 18,
      rule: (f) => f.jawDrop && f.browUp > 0.3,
      lines: [
        "Your mood looks {mood}. Your jaw just opened like the plot knocked on the door.",
        "I see {mood}. That face has theatre curtains, lighting, and a tiny orchestra.",
      ],
    },
    {
      id: "shocked",
      label: "Shocked",
      stability: 2,
      weight: 17,
      rule: (f) => f.jawDrop && f.eyeWide > 0.22,
      lines: [
        "Your mood looks {mood}. Your face just found out the universe has hidden fees.",
        "I see {mood}. Even your eyebrows look like they need a chair.",
      ],
    },
    {
      id: "laughing",
      label: "Laughing",
      stability: 2,
      weight: 17,
      rule: (f) => f.smile > 0.52 && f.jawOpen > 0.22 && f.cheekSquint > 0.16,
      lines: [
        "Your mood looks {mood}. Your face is throwing a tiny comedy festival.",
        "I see {mood}. Your cheeks are working overtime and honestly deserve snacks.",
      ],
    },
    {
      id: "radiantJoy",
      label: "Radiantly happy",
      stability: 2,
      weight: 16,
      rule: (f) => f.smile > 0.64,
      lines: [
        "Your mood looks {mood}. That smile is doing free electricity for the room.",
        "I see {mood}. Your grin just upgraded the lighting package.",
      ],
    },
    {
      id: "excited",
      label: "Excited",
      stability: 2,
      weight: 15,
      rule: (f) => f.smile > 0.42 && f.browUp > 0.22 && f.eyeWide > 0.16,
      lines: [
        "Your mood looks {mood}. Your eyebrows and smile have clearly formed a band.",
        "I see {mood}. Your face is acting like good news just arrived wearing sequins.",
      ],
    },
    {
      id: "amazed",
      label: "Amazed",
      stability: 2,
      weight: 15,
      rule: (f) => f.jawOpen > 0.34 && f.browUp > 0.22 && f.eyeWide > 0.14,
      lines: [
        "Your mood looks {mood}. Your face just opened a museum of wonder.",
        "I see {mood}. That expression says the plot became unexpectedly sparkly.",
      ],
    },
    {
      id: "awestruck",
      label: "Awestruck",
      stability: 2,
      weight: 15,
      rule: (f) => f.jawOpen > 0.3 && f.browUp > 0.28 && f.smile < 0.28,
      lines: [
        "Your mood looks {mood}. Your face is staring at the invisible fireworks.",
        "I see {mood}. The camera just whispered, this is a cathedral moment.",
      ],
    },
    {
      id: "surprised",
      label: "Surprised",
      stability: 2,
      weight: 14,
      rule: (f) => f.jawOpen > 0.27 || (f.eyeWide > 0.24 && f.browUp > 0.18),
      lines: [
        "Your mood looks {mood}. Your face just said, excuse me, what chapter is this?",
        "I see {mood}. That expression has a tiny exclamation mark wearing perfume.",
      ],
    },
    {
      id: "leftWink",
      label: "Left-wink playful",
      stability: 2,
      weight: 14,
      rule: (f) => f.leftWink,
      lines: [
        "Your mood looks {mood}. Your left eye just sent a confidential memo to charm.",
        "I see {mood}. That wink has the confidence of a person who found the shortcut.",
      ],
    },
    {
      id: "rightWink",
      label: "Right-wink playful",
      stability: 2,
      weight: 14,
      rule: (f) => f.rightWink,
      lines: [
        "Your mood looks {mood}. Your right eye just delivered a tiny plot twist.",
        "I see {mood}. That wink walked in like it had a reservation.",
      ],
    },
    {
      id: "kissy",
      label: "Kissy and glamorous",
      stability: 2,
      weight: 14,
      rule: (f) => f.pucker > 0.32,
      lines: [
        "Your mood looks {mood}. Your lips just applied for a perfume campaign.",
        "I see {mood}. That pout is giving glossy magazine with excellent hydration.",
      ],
    },
    {
      id: "cheekPuff",
      label: "Cheek-puff silly",
      stability: 2,
      weight: 14,
      rule: (f) => f.cheekPuff > 0.26,
      lines: [
        "Your mood looks {mood}. Your cheeks just became tiny luxury balloons.",
        "I see {mood}. Your face is storing joy in both cheeks for later.",
      ],
    },
    {
      id: "disgusted",
      label: "Disgusted",
      stability: 2,
      weight: 14,
      rule: (f) => f.noseSneer > 0.24 && f.browDown > 0.14,
      lines: [
        "Your mood looks {mood}. Your nose just rejected the entire agenda.",
        "I see {mood}. That face says, please remove this nonsense from my kingdom.",
      ],
    },
    {
      id: "noseScrunch",
      label: "Nose-scrunch doubtful",
      stability: 2,
      weight: 13,
      rule: (f) => f.noseSneer > 0.19,
      lines: [
        "Your mood looks {mood}. Your nose is doing quality control on reality.",
        "I see {mood}. That scrunch has standards and a tiny clipboard.",
      ],
    },
    {
      id: "suspicious",
      label: "Suspicious",
      stability: 2,
      weight: 13,
      rule: (f) => f.browDown > 0.21 && f.squint > 0.25,
      lines: [
        "Your mood looks {mood}. Your eyes are requesting evidence and possibly biscuits.",
        "I see {mood}. That face could interrogate a sandwich and get answers.",
      ],
    },
    {
      id: "sceptical",
      label: "Sceptical",
      stability: 2,
      weight: 13,
      rule: (f) => f.browAsymmetry > 0.13 && f.squint > 0.16,
      lines: [
        "Your mood looks {mood}. One eyebrow is clearly chairing the investigation.",
        "I see {mood}. Your face just said, interesting, but prove it beautifully.",
      ],
    },
    {
      id: "confused",
      label: "Confused",
      stability: 2,
      weight: 12,
      rule: (f) => f.browUp > 0.2 && f.squint > 0.18,
      lines: [
        "Your mood looks {mood}. Your eyebrows are opening tabs your brain did not request.",
        "I see {mood}. That face says the instructions came without furniture.",
      ],
    },
    {
      id: "puzzled",
      label: "Puzzled",
      stability: 2,
      weight: 12,
      rule: (f) => f.browInnerUp > 0.22 && f.squint > 0.14,
      lines: [
        "Your mood looks {mood}. Your face is solving a tiny crossword made of air.",
        "I see {mood}. That expression has a question mark in a velvet jacket.",
      ],
    },
    {
      id: "concerned",
      label: "Concerned",
      stability: 3,
      weight: 12,
      rule: (f) => f.browInnerUp > 0.28 && f.browDown > 0.12 && f.smile < 0.18,
      lines: [
        "Your mood looks {mood}. Your eyebrows are holding a small safety meeting.",
        "I see {mood}. Your face is checking if the universe read the manual.",
      ],
    },
    {
      id: "worried",
      label: "Worried",
      stability: 3,
      weight: 12,
      rule: (f) => f.browInnerUp > 0.32 && f.smile < 0.16 && f.jawOpen < 0.28,
      lines: [
        "Your mood looks {mood}. Your forehead is doing weather reports again.",
        "I see {mood}. Tell your eyebrows the committee can take a tea break.",
      ],
    },
    {
      id: "sad",
      label: "Sad",
      stability: 3,
      weight: 11,
      rule: (f) => f.frown > 0.15 || (f.browInnerUp > 0.3 && f.smile < 0.12),
      lines: [
        "Your mood looks {mood}. Even a soft cloud gets its own silver outline.",
        "I see {mood}. Your face is doing quiet rain, but the garden still counts.",
      ],
    },
    {
      id: "disappointed",
      label: "Disappointed",
      stability: 3,
      weight: 11,
      rule: (f) => f.frown > 0.11 && f.press > 0.18,
      lines: [
        "Your mood looks {mood}. Your mouth just sent reality back for corrections.",
        "I see {mood}. That face says the soup of life needs more seasoning.",
      ],
    },
    {
      id: "annoyed",
      label: "Annoyed",
      stability: 2,
      weight: 12,
      rule: (f) => f.browDown > 0.3 && f.press > 0.15,
      lines: [
        "Your mood looks {mood}. Your brow just opened a complaint department.",
        "I see {mood}. That face says, I am calm, but the spreadsheet is not innocent.",
      ],
    },
    {
      id: "serious",
      label: "Serious",
      stability: 3,
      weight: 10,
      rule: (f) => f.browDown > 0.27 && f.smile < 0.2,
      lines: [
        "Your mood looks {mood}. Your face has entered boardroom mode.",
        "I see {mood}. That expression could approve a budget and still look iconic.",
      ],
    },
    {
      id: "determined",
      label: "Determined",
      stability: 3,
      weight: 10,
      rule: (f) => f.press > 0.25 && f.browDown > 0.18 && f.smile < 0.25,
      lines: [
        "Your mood looks {mood}. Your lips just signed a contract with discipline.",
        "I see {mood}. That face says the mountain has been notified.",
      ],
    },
    {
      id: "unimpressed",
      label: "Unimpressed",
      stability: 3,
      weight: 10,
      rule: (f) => f.press > 0.25 && f.browDown > 0.16 && f.smile < 0.16,
      lines: [
        "Your mood looks {mood}. Your face just gave reality two stars.",
        "I see {mood}. That expression says, charming attempt, please revise.",
      ],
    },
    {
      id: "focused",
      label: "Focused",
      stability: 3,
      weight: 9,
      rule: (f) => f.squint > 0.24 && f.browDown < 0.25 && f.jawOpen < 0.18,
      lines: [
        "Your mood looks {mood}. Your eyes are turning into tiny laser pointers.",
        "I see {mood}. That face is doing mental origami.",
      ],
    },
    {
      id: "thoughtful",
      label: "Thoughtful",
      stability: 3,
      weight: 9,
      rule: (f) => f.lipRoll > 0.2 && f.browDown < 0.25,
      lines: [
        "Your mood looks {mood}. Your lips are buffering a very stylish idea.",
        "I see {mood}. That face is opening seventeen brain tabs with elegance.",
      ],
    },
    {
      id: "lipRollThink",
      label: "Deep in thought",
      stability: 3,
      weight: 9,
      rule: (f) => f.lipRoll > 0.26,
      lines: [
        "Your mood looks {mood}. Your mouth just whispered, let me calculate the sparkle.",
        "I see {mood}. That expression has calculator energy with better cheekbones.",
      ],
    },
    {
      id: "secretive",
      label: "Secretive",
      stability: 3,
      weight: 9,
      rule: (f) => f.press > 0.32 && f.smile > 0.16,
      lines: [
        "Your mood looks {mood}. Your lips are guarding classified gossip.",
        "I see {mood}. That face says, I know something, and the curtains agree.",
      ],
    },
    {
      id: "holdingLaugh",
      label: "Holding back laughter",
      stability: 3,
      weight: 9,
      rule: (f) => f.press > 0.26 && f.cheekSquint > 0.18 && f.smile > 0.2,
      lines: [
        "Your mood looks {mood}. Your mouth is trying to behave and failing beautifully.",
        "I see {mood}. That face is one joke away from fireworks.",
      ],
    },
    {
      id: "playful",
      label: "Playful",
      stability: 2,
      weight: 10,
      rule: (f) => f.smile > 0.32 && f.smileAsymmetry > 0.12,
      lines: [
        "Your mood looks {mood}. That half-smile just put glitter in the room.",
        "I see {mood}. Your grin looks like it knows where the fun is stored.",
      ],
    },
    {
      id: "cheeky",
      label: "Cheeky",
      stability: 2,
      weight: 10,
      rule: (f) => f.cheekSquint > 0.25 && f.smile > 0.28,
      lines: [
        "Your mood looks {mood}. Your cheeks are trying to act innocent. Noted.",
        "I see {mood}. That smile has backstage access to the good gossip.",
      ],
    },
    {
      id: "coySmile",
      label: "Coy",
      stability: 3,
      weight: 8,
      rule: (f) => f.softSmile && f.press > 0.16,
      lines: [
        "Your mood looks {mood}. That smile entered quietly and stole the furniture.",
        "I see {mood}. Your face is doing tiny charm with a velvet rope.",
      ],
    },
    {
      id: "shy",
      label: "Shy",
      stability: 3,
      weight: 8,
      rule: (f) => f.softSmile && f.browUp < 0.2 && f.press > 0.14,
      lines: [
        "Your mood looks {mood}. That smile is peeking from behind a very polite curtain.",
        "I see {mood}. Your face brought a small lantern instead of a spotlight.",
      ],
    },
    {
      id: "confident",
      label: "Confident",
      stability: 3,
      weight: 8,
      rule: (f) => f.smile > 0.34 && f.browDown < 0.15 && f.jawOpen < 0.18,
      lines: [
        "Your mood looks {mood}. Your face just walked in with its own theme music.",
        "I see {mood}. That smile has a five-year plan and excellent lighting.",
      ],
    },
    {
      id: "proud",
      label: "Proud",
      stability: 3,
      weight: 8,
      rule: (f) => f.smile > 0.28 && f.browUp < 0.16 && f.press < 0.2,
      lines: [
        "Your mood looks {mood}. Your face is quietly polishing a trophy.",
        "I see {mood}. That expression says, yes, I did bring the sparkle.",
      ],
    },
    {
      id: "amused",
      label: "Amused",
      stability: 3,
      weight: 8,
      rule: (f) => f.smile > 0.34 && f.cheekSquint > 0.12,
      lines: [
        "Your mood looks {mood}. Your cheeks are laughing before the rest of you.",
        "I see {mood}. That expression has a tiny comedy club under the eyes.",
      ],
    },
    {
      id: "gentleHappy",
      label: "Gently happy",
      stability: 3,
      weight: 7,
      rule: (f) => f.softSmile,
      lines: [
        "Your mood looks {mood}. That smile is soft enough to fold into a postcard.",
        "I see {mood}. Your face is glowing at library volume.",
      ],
    },
    {
      id: "warm",
      label: "Warm",
      stability: 4,
      weight: 6,
      rule: (f) => f.smile > 0.2 && f.jawOpen < 0.16 && f.browDown < 0.16,
      lines: [
        "Your mood looks {mood}. Your face feels like a small lamp in a quiet room.",
        "I see {mood}. That expression is tea, sunlight, and good manners.",
      ],
    },
    {
      id: "brightAlert",
      label: "Bright and alert",
      stability: 3,
      weight: 7,
      rule: (f) => f.eyeWide > 0.18 && f.browUp > 0.16 && f.jawOpen < 0.22,
      lines: [
        "Your mood looks {mood}. Your eyes just switched on the chandelier.",
        "I see {mood}. That face is awake enough to negotiate with Monday.",
      ],
    },
    {
      id: "curious",
      label: "Curious",
      stability: 3,
      weight: 7,
      rule: (f) => f.browUp > 0.22 && f.jawOpen < 0.24 && f.squint < 0.25,
      lines: [
        "Your mood looks {mood}. Your eyebrows are gently opening the door.",
        "I see {mood}. That face says, continue, but make it interesting.",
      ],
    },
    {
      id: "curiousTiltLeft",
      label: "Curious left tilt",
      stability: 3,
      weight: 7,
      rule: (f) => f.headTiltLeft && f.browUp > 0.12,
      lines: [
        "Your mood looks {mood}. Your head tilted like the story finally got flavour.",
        "I see {mood}. That angle is a question mark with cheekbones.",
      ],
    },
    {
      id: "curiousTiltRight",
      label: "Curious right tilt",
      stability: 3,
      weight: 7,
      rule: (f) => f.headTiltRight && f.browUp > 0.12,
      lines: [
        "Your mood looks {mood}. Your head just leaned toward the interesting part.",
        "I see {mood}. That tilt says, yes, I am emotionally subscribed.",
      ],
    },
    {
      id: "sideEyeLeft",
      label: "Left side-eye",
      stability: 3,
      weight: 7,
      rule: (f) => f.headTurnLeft && f.squint > 0.14,
      lines: [
        "Your mood looks {mood}. Your left profile is reviewing the situation carefully.",
        "I see {mood}. That side glance has tiny detective shoes.",
      ],
    },
    {
      id: "sideEyeRight",
      label: "Right side-eye",
      stability: 3,
      weight: 7,
      rule: (f) => f.headTurnRight && f.squint > 0.14,
      lines: [
        "Your mood looks {mood}. Your right profile just asked for receipts.",
        "I see {mood}. That side glance could audit a rumour.",
      ],
    },
    {
      id: "leftProfile",
      label: "Left-profile calm",
      stability: 4,
      weight: 5,
      rule: (f) => f.headTurnLeft && f.squint <= 0.16,
      lines: [
        "Your mood looks {mood}. Your left side just found its album cover.",
        "I see {mood}. That profile is calm, cinematic, and very aware of the lens.",
      ],
    },
    {
      id: "rightProfile",
      label: "Right-profile calm",
      stability: 4,
      weight: 5,
      rule: (f) => f.headTurnRight && f.squint <= 0.16,
      lines: [
        "Your mood looks {mood}. Your right side just entered portrait mode.",
        "I see {mood}. That profile has quiet cinema energy.",
      ],
    },
    {
      id: "speechless",
      label: "Speechless",
      stability: 3,
      weight: 6,
      rule: (f) => f.jawOpen > 0.3 && f.browUp < 0.18 && f.smile < 0.18,
      lines: [
        "Your mood looks {mood}. Your mouth opened and the words took annual leave.",
        "I see {mood}. That expression says the brain is still downloading.",
      ],
    },
    {
      id: "mouthOpenWonder",
      label: "Wondering",
      stability: 3,
      weight: 6,
      rule: (f) => f.jawOpen > 0.22 && f.browUp > 0.12,
      lines: [
        "Your mood looks {mood}. Your face is peeking into the next chapter.",
        "I see {mood}. That expression has a little window open.",
      ],
    },
    {
      id: "impressed",
      label: "Impressed",
      stability: 3,
      weight: 6,
      rule: (f) => f.smile > 0.24 && f.browUp > 0.18 && f.jawOpen < 0.26,
      lines: [
        "Your mood looks {mood}. Your eyebrows just gave a polite standing ovation.",
        "I see {mood}. That face says, not bad, sparkle department approved.",
      ],
    },
    {
      id: "romanticPout",
      label: "Soft pout",
      stability: 3,
      weight: 6,
      rule: (f) => f.pucker > 0.22 && f.smile < 0.2,
      lines: [
        "Your mood looks {mood}. Your lips just dimmed the lights for atmosphere.",
        "I see {mood}. That pout is gentle, glossy, and pretending not to pose.",
      ],
    },
    {
      id: "hesitant",
      label: "Hesitant",
      stability: 3,
      weight: 6,
      rule: (f) => f.mouthShrug > 0.22 && f.browInnerUp > 0.12,
      lines: [
        "Your mood looks {mood}. Your mouth is standing at the door with one shoe on.",
        "I see {mood}. That expression says, maybe, but make it convincing.",
      ],
    },
    {
      id: "unsureMouth",
      label: "Unsure",
      stability: 3,
      weight: 6,
      rule: (f) => f.mouthShrug > 0.22 || (f.press > 0.2 && f.browUp > 0.12),
      lines: [
        "Your mood looks {mood}. Your lips are forming a committee before answering.",
        "I see {mood}. That face says, I need one more clue and maybe a biscuit.",
      ],
    },
    {
      id: "sleepyBlink",
      label: "Sleepy blink",
      stability: 2,
      weight: 6,
      rule: (f) => f.blink > 0.74 && f.browUp < 0.18,
      lines: [
        "Your mood looks {mood}. Your eyelids just requested a tiny holiday.",
        "I see {mood}. That blink arrived wearing pyjamas and dignity.",
      ],
    },
    {
      id: "tired",
      label: "Tired",
      stability: 4,
      weight: 5,
      rule: (f) => f.squint > 0.24 && f.smile < 0.14 && f.browUp < 0.16,
      lines: [
        "Your mood looks {mood}. Your eyes are sending a polite email to the pillow.",
        "I see {mood}. That face says the battery is at twelve percent but still stylish.",
      ],
    },
    {
      id: "peaceful",
      label: "Peaceful",
      stability: 5,
      weight: 5,
      rule: (f) => f.neutral && f.browDown < 0.1,
      lines: [
        "Your mood looks {mood}. Your face is doing a quiet lake impression.",
        "I see {mood}. Even the camera softened its shoulders.",
      ],
    },
    {
      id: "serene",
      label: "Serene",
      stability: 5,
      weight: 5,
      rule: (f) => f.neutral && f.squint < 0.12 && f.browInnerUp < 0.16,
      lines: [
        "Your mood looks {mood}. Your expression has spa music without needing speakers.",
        "I see {mood}. That calm is wearing silk slippers.",
      ],
    },
    {
      id: "calm",
      label: "Calm",
      stability: 5,
      weight: 4,
      rule: (f) => f.neutral,
      lines: [
        "Your mood looks {mood}. Your face is resting, but the charm is still on duty.",
        "I see {mood}. That expression is a clean desk in human form.",
      ],
    },
    {
      id: "neutralCalm",
      label: "Neutral calm",
      stability: 6,
      weight: 3,
      rule: (f) => f.smile < 0.22 && f.jawOpen < 0.2,
      lines: [
        "Your mood looks {mood}. The camera says you are quietly buffering in elegance.",
        "I see {mood}. Your face is giving peaceful homepage before the animation starts.",
      ],
    },
  ];
}

const pageStyles = `
  .mood-page {
    min-height: 100vh;
    background: linear-gradient(135deg, ${BG_DARK} 0%, ${BG} 52%, ${BG_LIGHT} 100%);
    color: ${TEXT};
    padding: 22px;
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
  }

  .mood-shell {
    width: min(1520px, 100%);
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 22px;
    align-items: start;
  }

  .main-card,
  .ad-card {
    background: ${CARD};
    border: 1px solid ${CARD_BORDER};
    border-radius: 28px;
    box-shadow: ${SHADOW};
    backdrop-filter: blur(14px);
  }

  .main-card {
    padding: 22px;
  }

  .hero-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }

  .eyebrow,
  .small-label,
  .ad-label,
  .joke-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1.4px;
    color: ${ACCENT};
    font-weight: 900;
  }

  h1 {
    margin: 8px 0 0;
    font-size: clamp(34px, 6vw, 58px);
    line-height: 0.95;
    letter-spacing: -1.8px;
  }

  .intro {
    margin: 12px 0 0;
    color: ${TEXT_SOFT};
    line-height: 1.7;
    max-width: 820px;
    font-size: 16px;
  }

  .engine-card {
    background: rgba(255,255,255,0.14);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 18px;
    padding: 12px 15px;
    min-width: 180px;
  }

  .engine-card span {
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    opacity: 0.8;
    margin-bottom: 6px;
  }

  .engine-card strong {
    font-size: 15px;
    font-weight: 900;
  }

  .camera-wrap {
    position: relative;
    border-radius: 26px;
    overflow: hidden;
    background: #08332f;
    aspect-ratio: 16 / 9;
    max-height: 620px;
    box-shadow: 0 18px 52px rgba(0,0,0,0.24);
    margin-bottom: 18px;
  }

  .mirror-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: scaleX(-1);
    display: block;
  }

  .start-overlay {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: grid;
    place-items: center;
    padding: 18px;
    background:
      radial-gradient(circle at top left, rgba(255,224,130,0.26), transparent 34%),
      linear-gradient(135deg, rgba(0,65,58,0.9), rgba(0,120,110,0.72));
  }

  .start-panel {
    width: min(440px, 100%);
    background: rgba(255,255,255,0.16);
    border: 1px solid rgba(255,255,255,0.24);
    border-radius: 26px;
    padding: 26px;
    text-align: center;
    box-shadow: 0 20px 55px rgba(0,0,0,0.26);
  }

  .spark {
    width: 52px;
    height: 52px;
    border-radius: 999px;
    margin: 0 auto 12px;
    display: grid;
    place-items: center;
    background: rgba(255,224,130,0.22);
    border: 1px solid rgba(255,224,130,0.45);
    color: ${ACCENT};
    font-size: 26px;
  }

  .start-panel h2 {
    margin: 0 0 8px;
    font-size: 28px;
  }

  .start-panel p {
    margin: 0 0 18px;
    color: ${TEXT_SOFT};
    line-height: 1.6;
  }

  .pill-row {
    position: absolute;
    left: 18px;
    top: 18px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    z-index: 4;
    max-width: calc(100% - 36px);
  }

  .status-pill {
    background: rgba(255,255,255,0.16);
    border: 1px solid rgba(255,255,255,0.2);
    color: #fff;
    border-radius: 999px;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 900;
    backdrop-filter: blur(8px);
  }

  .status-pill-accent {
    background: rgba(255,224,130,0.24);
    border-color: rgba(255,224,130,0.4);
  }

  .mood-result-card {
    background: rgba(255,255,255,0.18);
    border: 1px solid ${CARD_BORDER};
    border-radius: 24px;
    padding: 24px;
    box-shadow: 0 14px 34px rgba(0,0,0,0.14);
  }

  .mood-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-top: 14px;
    margin-bottom: 18px;
  }

  .mood-name {
    font-size: clamp(30px, 5vw, 48px);
    line-height: 1;
    font-weight: 950;
    letter-spacing: -1.4px;
    text-shadow: 0 4px 18px rgba(0,0,0,0.16);
  }

  .confidence-line {
    margin-top: 10px;
    color: ${TEXT_SOFT};
    font-weight: 850;
    font-size: 15px;
  }

  .confidence-ring {
    width: 86px;
    height: 86px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    background:
      radial-gradient(circle, rgba(255,255,255,0.18), rgba(255,255,255,0.08)),
      conic-gradient(from 180deg, rgba(255,224,130,0.95), rgba(255,255,255,0.14), rgba(255,224,130,0.75));
    border: 1px solid rgba(255,255,255,0.28);
    box-shadow: 0 14px 30px rgba(0,0,0,0.16);
  }

  .confidence-ring span {
    width: 66px;
    height: 66px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: rgba(0,94,85,0.72);
    color: #fff;
    font-weight: 950;
    font-size: 20px;
  }

  .spoken-line {
    font-size: clamp(22px, 3.5vw, 35px);
    line-height: 1.3;
    font-weight: 950;
    letter-spacing: -0.7px;
    margin-bottom: 14px;
  }

  .mood-result-card p {
    margin: 0;
    color: ${TEXT_SOFT};
    line-height: 1.8;
    font-size: 15px;
  }

  .controls-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-top: 18px;
  }

  .info-card {
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.16);
    border-radius: 16px;
    padding: 14px 16px;
    min-height: 88px;
  }

  .info-card div {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    opacity: 0.78;
    margin-bottom: 6px;
  }

  .info-card strong {
    display: block;
    font-size: 15px;
    line-height: 1.45;
    font-weight: 850;
    word-break: break-word;
  }

  .voice-panel {
    margin-top: 18px;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.16);
    border-radius: 22px;
    padding: 18px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(260px, 420px);
    gap: 18px;
    align-items: center;
  }

  .voice-panel p {
    margin: 8px 0 0;
    color: ${TEXT_SOFT};
    line-height: 1.65;
    font-size: 14px;
  }

  .voice-actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  select,
  button {
    width: 100%;
    border: 0;
    border-radius: 999px;
    padding: 13px 16px;
    font-weight: 900;
    font-size: 14px;
    outline: none;
  }

  select {
    background: rgba(255,255,255,0.94);
    color: #063b36;
  }

  button {
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
  }

  button:hover {
    transform: translateY(-1px);
  }

  .primary-btn {
    background: ${ACCENT};
    color: #063b36;
    box-shadow: 0 14px 30px rgba(0,0,0,0.2);
  }

  .secondary-btn {
    background: rgba(255,255,255,0.92);
    color: #063b36;
  }

  .danger-btn {
    background: rgba(255,110,110,0.92);
    color: #fff;
  }

  .error-text {
    margin: 16px 0 0;
    color: #ffe3e3;
    line-height: 1.6;
    font-weight: 800;
  }

  .ad-card {
    padding: 16px;
    position: sticky;
    top: 22px;
  }

  .ad-label {
    margin-bottom: 10px;
  }

  .ad-link {
    display: block;
    text-decoration: none;
    color: inherit;
    border-radius: 18px;
    overflow: hidden;
    background: rgba(255,255,255,0.96);
    box-shadow: 0 14px 36px rgba(0,0,0,0.18);
  }

  .ad-link img {
    width: 100%;
    display: block;
    object-fit: cover;
  }

  .ad-copy {
    margin-top: 14px;
  }

  .ad-copy h2 {
    font-size: 20px;
    font-weight: 900;
    line-height: 1.2;
    margin: 0 0 8px;
  }

  .ad-copy p {
    margin: 0;
    color: ${TEXT_SOFT};
    line-height: 1.7;
    font-size: 14px;
  }

  @media (max-width: 1100px) {
    .mood-shell {
      grid-template-columns: 1fr;
    }

    .ad-card {
      position: static;
    }

    .controls-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .voice-panel {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 720px) {
    .mood-page {
      padding: 12px;
    }

    .main-card,
    .ad-card {
      border-radius: 22px;
    }

    .main-card {
      padding: 14px;
    }

    h1 {
      letter-spacing: -1.2px;
    }

    .intro {
      font-size: 14px;
    }

    .engine-card {
      width: 100%;
    }

    .camera-wrap {
      border-radius: 20px;
      aspect-ratio: 3 / 4;
      max-height: none;
    }

    .pill-row {
      left: 10px;
      top: 10px;
      right: 10px;
      gap: 7px;
      max-width: calc(100% - 20px);
    }

    .status-pill {
      font-size: 11px;
      padding: 7px 9px;
    }

    .mood-result-card {
      padding: 18px;
      border-radius: 20px;
    }

    .mood-row {
      align-items: flex-start;
    }

    .confidence-ring {
      width: 70px;
      height: 70px;
    }

    .confidence-ring span {
      width: 54px;
      height: 54px;
      font-size: 17px;
    }

    .controls-grid {
      grid-template-columns: 1fr;
    }

    .voice-panel {
      padding: 16px;
      border-radius: 20px;
    }

    .start-panel {
      padding: 20px;
      border-radius: 22px;
    }

    .start-panel h2 {
      font-size: 24px;
    }
  }

  @media (max-width: 420px) {
    .mood-page {
      padding: 8px;
    }

    .main-card {
      padding: 12px;
    }

    .camera-wrap {
      aspect-ratio: 9 / 13;
    }

    .spoken-line {
      font-size: 22px;
    }

    .mood-name {
      font-size: 30px;
    }

    select,
    button {
      border-radius: 18px;
    }
  }
`;
